import { Component } from '@theme/component';

const PANEL_VISIBLE_CLASS = 'brick-info-tooltip__panel--visible';
const VIEWPORT_MARGIN_PX = 24;
const HOVER_CLOSE_DELAY_MS = 140;

/** @type {Set<BrickInfoTooltip>} */
const instances = new Set();

/** @type {HTMLElement | null | undefined} */
let headerSection;

/**
 * @typedef {Object} BrickInfoTooltipRefs
 * @property {HTMLButtonElement} toggle
 * @property {HTMLElement} panel
 */

/**
 * Info-icon tooltip: click toggles, hover on pointer devices, Escape / outside click close.
 *
 * @extends {Component<BrickInfoTooltipRefs>}
 */
class BrickInfoTooltip extends Component {
  requiredRefs = ['toggle', 'panel'];

  /** @type {AbortController | null} */
  #lifetimeController = null;

  /** @type {AbortController | null} */
  #openController = null;

  /** @type {number | null} */
  #hideTimeout = null;

  /** @type {number | null} */
  #positionFrame = null;

  #pinned = false;

  connectedCallback() {
    super.connectedCallback();
    this.#lifetimeController?.abort();
    this.#lifetimeController = new AbortController();
    const { signal } = this.#lifetimeController;
    const { toggle, panel } = this.refs;

    instances.add(this);
    panel.hidden = true;
    toggle.setAttribute('aria-expanded', 'false');
    toggle.addEventListener('click', this.#onToggleClick, { signal });
    this.addEventListener('focusout', this.#onFocusOut, { signal });

    if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      this.addEventListener('pointerenter', this.#onPointerEnter, { signal });
      this.addEventListener('pointerleave', this.#onPointerLeave, { signal });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    instances.delete(this);
    this.#lifetimeController?.abort();
    this.#lifetimeController = null;
    this.#unbindOpenListeners();
    this.#clearHideTimeout();
    this.#cancelPositionFrame();
  }

  get #isOpen() {
    return !this.refs.panel.hidden;
  }

  /**
   * @param {MouseEvent} event
   */
  #onToggleClick = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (this.#isOpen && this.#pinned) {
      this.hide();
      return;
    }

    this.show(true);
  };

  /**
   * @param {MouseEvent} event
   */
  #onDocumentClick = (event) => {
    if (!this.#isOpen) return;
    if (event.target instanceof Node && this.contains(event.target)) return;

    this.hide();
  };

  /**
   * @param {KeyboardEvent} event
   */
  #onKeydown = (event) => {
    if (event.key !== 'Escape' || !this.#isOpen) return;

    event.preventDefault();
    event.stopPropagation();
    this.hide({ restoreFocus: true });
  };

  #onPointerEnter = () => {
    this.#clearHideTimeout();
    if (!this.#isOpen) this.show(false);
  };

  #onPointerLeave = () => {
    this.#clearHideTimeout();
    this.#hideTimeout = window.setTimeout(() => {
      this.#hideTimeout = null;
      if (!this.#pinned && !this.matches(':focus-within')) this.hide();
    }, HOVER_CLOSE_DELAY_MS);
  };

  #onFocusOut = () => {
    queueMicrotask(() => {
      if (!this.#isOpen || this.matches(':focus-within') || this.matches(':hover')) return;
      this.hide();
    });
  };

  /**
   * @param {boolean} pinned
   */
  show(pinned = false) {
    this.#clearHideTimeout();

    for (const other of instances) {
      if (other !== this) other.hide();
    }

    for (const tooltip of document.querySelectorAll('[data-upsell-tooltip].brick-upsell__tooltip--visible')) {
      if (!(tooltip instanceof HTMLElement)) continue;

      tooltip.classList.remove('brick-upsell__tooltip--visible');
      tooltip.style.removeProperty('--tooltip-shift');
      const toggle =
        tooltip.parentElement?.querySelector('[data-upsell-tooltip-toggle]') ||
        tooltip.closest('[data-upsell-container]')?.querySelector('[data-upsell-tooltip-toggle]');
      toggle?.setAttribute('aria-expanded', 'false');
    }

    const { panel, toggle } = this.refs;
    this.#pinned = pinned;
    panel.hidden = false;
    panel.classList.add(PANEL_VISIBLE_CLASS);
    panel.removeAttribute('data-positioned');
    toggle.setAttribute('aria-expanded', 'true');
    this.#bindOpenListeners();
    this.#positionPanel();
  }

  /**
   * @param {{ restoreFocus?: boolean }} [options]
   */
  hide({ restoreFocus = false } = {}) {
    this.#clearHideTimeout();
    this.#cancelPositionFrame();
    this.#unbindOpenListeners();

    const { panel, toggle } = this.refs;
    this.#pinned = false;
    panel.classList.remove(PANEL_VISIBLE_CLASS);
    panel.removeAttribute('data-positioned');
    panel.removeAttribute('data-placement');
    panel.style.removeProperty('--tooltip-shift');
    panel.style.removeProperty('--tooltip-max-height');
    panel.hidden = true;
    toggle.setAttribute('aria-expanded', 'false');

    if (restoreFocus) toggle.focus();
  }

  #bindOpenListeners() {
    this.#openController?.abort();
    this.#openController = new AbortController();
    const { signal } = this.#openController;

    document.addEventListener('click', this.#onDocumentClick, { signal });
    document.addEventListener('keydown', this.#onKeydown, { signal });
    window.addEventListener('resize', this.#schedulePosition, { signal });
    window.addEventListener('scroll', this.#schedulePosition, { signal, capture: true, passive: true });
  }

  #unbindOpenListeners() {
    this.#openController?.abort();
    this.#openController = null;
  }

  #schedulePosition = () => {
    if (!this.#isOpen || this.#positionFrame !== null) return;

    this.#positionFrame = requestAnimationFrame(() => {
      this.#positionFrame = null;
      this.#positionPanel();
    });
  };

  #positionPanel() {
    if (!this.#isOpen) return;

    const { panel } = this.refs;
    const preferredPlacement = this.dataset.preferredPlacement;

    panel.removeAttribute('data-positioned');
    panel.style.removeProperty('--tooltip-shift');
    panel.style.removeProperty('--tooltip-max-height');

    if (preferredPlacement === 'above' || preferredPlacement === 'below') {
      const anchorRect = this.getBoundingClientRect();
      const naturalHeight = panel.getBoundingClientRect().height;
      const gap = 8;
      const topBoundary = this.#getTopBoundary();
      const spaceAbove = Math.max(0, anchorRect.top - topBoundary - gap);
      const spaceBelow = Math.max(0, window.innerHeight - anchorRect.bottom - VIEWPORT_MARGIN_PX - gap);
      const preferredSpace = preferredPlacement === 'above' ? spaceAbove : spaceBelow;
      const alternateSpace = preferredPlacement === 'above' ? spaceBelow : spaceAbove;
      let placement = preferredPlacement;

      if (naturalHeight > preferredSpace && (naturalHeight <= alternateSpace || alternateSpace > preferredSpace)) {
        placement = preferredPlacement === 'above' ? 'below' : 'above';
      }

      panel.dataset.placement = placement;
      const availableHeight = placement === 'above' ? spaceAbove : spaceBelow;

      if (naturalHeight > availableHeight) {
        panel.style.setProperty('--tooltip-max-height', `${availableHeight}px`);
      }
    } else {
      panel.removeAttribute('data-placement');
    }

    const rect = panel.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    let shift = 0;

    if (rect.left < VIEWPORT_MARGIN_PX) {
      shift = VIEWPORT_MARGIN_PX - rect.left;
    } else if (rect.right > viewportWidth - VIEWPORT_MARGIN_PX) {
      shift = viewportWidth - VIEWPORT_MARGIN_PX - rect.right;
    }

    if (shift !== 0) panel.style.setProperty('--tooltip-shift', `${shift}px`);
    panel.setAttribute('data-positioned', '');
  }

  #getTopBoundary() {
    if (headerSection === undefined) {
      const header = document.querySelector('.header-section');
      headerSection = header instanceof HTMLElement ? header : null;
    }

    if (!headerSection) return VIEWPORT_MARGIN_PX;

    const rect = headerSection.getBoundingClientRect();
    if (rect.bottom <= 0 || rect.top >= window.innerHeight) return VIEWPORT_MARGIN_PX;

    return Math.max(VIEWPORT_MARGIN_PX, rect.bottom + 8);
  }

  #clearHideTimeout() {
    if (this.#hideTimeout === null) return;

    window.clearTimeout(this.#hideTimeout);
    this.#hideTimeout = null;
  }

  #cancelPositionFrame() {
    if (this.#positionFrame === null) return;

    cancelAnimationFrame(this.#positionFrame);
    this.#positionFrame = null;
  }
}

if (!customElements.get('brick-info-tooltip')) {
  customElements.define('brick-info-tooltip', BrickInfoTooltip);
}
